// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import * as VehicleScheduleActions from '../actions/vehicle-schedule.actions';
import { VehicleScheduleService } from '../../vehicles/service/vehicle-schedule.service';
import { NotificationService } from '@fc-core';
import { getVehicleSchedule } from '../selectors/vehicle-schedule.selectors';
import { Store } from '@ngrx/store';
import { loadVehicleData } from '../actions';
import { getErrorMessage } from '@fc-core/services/notification.utils';

@Injectable()
export class VehicleScheduleEffects {
  loadVehicleSchedules$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VehicleScheduleActions.loadVehicleSchedules),
      concatMap(({ options }) =>
        this.service.getVehicleSchedule(options).pipe(
          map((data) =>
            VehicleScheduleActions.loadVehicleSchedulesSuccess({
              eventList: data,
            }),
          ),
          catchError((error) =>
            of(VehicleScheduleActions.vehicleSchedulesError({ error })),
          ),
        ),
      ),
    );
  });

  addVehicleSchedules$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VehicleScheduleActions.addVehicleSchedule),
      concatMap(({ event }) =>
        this.service.addVehicleSchedule(event).pipe(
          switchMap((data) => {
            this.notifications.showSuccess(
              'Vehicle Schedule Added Successfully',
            );
            return [
              VehicleScheduleActions.addVehicleScheduleSuccess({ event: data }),
              loadVehicleData({
                vehicleId: event.vehicle,
                options: ['operators'],
              }),
            ];
          }),
          catchError((error) =>
            of(VehicleScheduleActions.vehicleSchedulesError({ error })),
          ),
        ),
      ),
    );
  });

  deleteVehicleSchedules$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VehicleScheduleActions.deleteVehicleSchedule),
      concatMap(({ event }) =>
        this.service.deleteVehicleSchedule(event).pipe(
          switchMap(() => {
            this.notifications.showSuccess(
              'Vehicle Schedule Deleted Successfully',
            );
            return [
              VehicleScheduleActions.deleteVehicleScheduleSuccess({ event }),
              loadVehicleData({
                vehicleId: event.vehicle,
                options: ['operators'],
              }),
            ];
          }),
          catchError((error) =>
            of(VehicleScheduleActions.vehicleSchedulesError({ error })),
          ),
        ),
      ),
    );
  });

  updateVehicleSchedule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VehicleScheduleActions.updateVehicleSchedule),
      concatMap(({ event }) =>
        this.service.updateVehicleSchedule(event).pipe(
          withLatestFrom(this.store.select(getVehicleSchedule)),
          switchMap(([updatedEvent]) => {
            this.notifications.showSuccess(
              'Vehicle Schedule Updated Successfully',
            );

            return [
              loadVehicleData({
                vehicleId: event.vehicle,
                options: ['operators'],
              }),
              VehicleScheduleActions.updateVehicleScheduleSuccess({
                event: updatedEvent,
              }),
            ];
          }),
          catchError((error) =>
            of(VehicleScheduleActions.vehicleSchedulesError({ error })),
          ),
        ),
      ),
    );
  });

  scheduleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VehicleScheduleActions.vehicleSchedulesError),
        switchMap(({ error }) => {
          this.notifications.showError(getErrorMessage(error));
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private service: VehicleScheduleService,
    private store: Store,
    private notifications: NotificationService,
  ) {}
}
